/**
 * A trilha e o quadro exibido — funções puras (testadas em `track.test.ts`).
 *
 * A camada de embarcações anda junto com o loop, como a chuva: quando alguém
 * arrasta a barra de tempo, o navio tem de estar onde estava naquele instante.
 *
 * Duas regras valem mais que a conveniência de sempre mostrar alguma coisa:
 *
 * 1. **Nunca interpolar através de uma lacuna.** Se a embarcação saiu do
 *    alcance por três horas e voltou, ligar as duas pontas desenharia uma
 *    reta atravessando terra firme — uma rota que não aconteceu.
 * 2. **Longe demais do quadro é "não sei", não "última posição".** Mostrar a
 *    posição de ontem no quadro de agora é pior que não mostrar nada.
 */
import type { Amostra } from './types';

/**
 * Distância máxima no tempo entre o quadro exibido e a amostra usada.
 *
 * O pipeline publica a cada ~15 min; o dobro disso dá folga para um ciclo
 * perdido sem passar a mostrar posição velha como se fosse atual.
 */
export const TOLERANCIA_MS = 35 * 60 * 1000;

/** Acima disso, duas amostras não se ligam: houve silêncio no meio. */
export const LACUNA_MS = 30 * 60 * 1000;

/**
 * Marca (5º campo) da amostra que fecha uma parada: a embarcação ficou no
 * ponto da amostra anterior, ouvida sem lacuna, até este instante.
 * `PARADA` e `LACUNA_MS` repetem as de `AmaView-dados/scripts/frota.mjs`:
 * mudam juntas.
 */
export const PARADA = 1;

export interface Posicao {
  lat: number;
  lon: number;
  sog: number | null;
  ms: number;
  /** Quanto a amostra está distante do instante pedido, em ms. */
  distanciaMs: number;
}

/**
 * Posição da embarcação no instante pedido, ou `null` quando não dá para
 * afirmar nada.
 *
 * Busca binária: a trilha chega ordenada do pipeline, e com 96 amostras a
 * diferença é irrelevante — mas a camada chama isto uma vez por embarcação a
 * cada quadro do loop, e a ordem certa não custa nada.
 */
export function posicaoEm(trilha: readonly Amostra[] | undefined, instante: number | null): Posicao | null {
  if (!trilha || trilha.length === 0 || instante === null || !Number.isFinite(instante)) return null;

  const i = maisProxima(trilha, instante);
  const a = trilha[i];
  if (!a) return null;

  // Dentro de uma parada (par começo → fim marcado), a embarcação estava ali:
  // o pipeline só forma o par com escuta contínua, então não é interpolação
  // através de lacuna. Sem isto, um navio atracado há horas sumia do loop em
  // todo quadro a mais de 35 min das duas pontas.
  const fim = trilha[i]![0] >= instante ? i : i + 1;
  const b = trilha[fim];
  const antes = trilha[fim - 1];
  if (b && antes && b[4] === PARADA && antes[0] <= instante && instante <= b[0]) {
    return { lat: b[1], lon: b[2], sog: b[3], ms: instante, distanciaMs: 0 };
  }

  const dist = Math.abs(a[0] - instante);
  if (dist > TOLERANCIA_MS) return null;

  return { lat: a[1], lon: a[2], sog: a[3], ms: a[0], distanciaMs: dist };
}

/** Índice da amostra mais próxima do instante. */
export function maisProxima(trilha: readonly Amostra[], instante: number): number {
  let lo = 0;
  let hi = trilha.length - 1;
  while (lo < hi) {
    const meio = (lo + hi) >> 1;
    if (trilha[meio]![0] < instante) lo = meio + 1;
    else hi = meio;
  }
  // `lo` é a primeira amostra >= instante; a anterior pode estar mais perto.
  const antes = lo > 0 ? lo - 1 : 0;
  const dA = Math.abs(trilha[antes]![0] - instante);
  const dB = Math.abs(trilha[lo]![0] - instante);
  return dA <= dB ? antes : lo;
}

/**
 * Parte a trilha nos trechos contínuos, para desenhar cada um como uma linha
 * própria. Amostras separadas por mais que `LACUNA_MS` não entram no mesmo
 * trecho.
 *
 * Trechos de um ponto só são descartados: uma linha precisa de dois.
 */
export function trechos(trilha: readonly Amostra[] | undefined, ate: number | null = null): Amostra[][] {
  if (!trilha || trilha.length < 2) return [];
  const usaveis = ate === null ? trilha : trilha.filter((a) => a[0] <= ate);
  if (usaveis.length < 2) return [];

  const saida: Amostra[][] = [];
  let atual: Amostra[] = [usaveis[0]!];
  for (let i = 1; i < usaveis.length; i++) {
    const a = usaveis[i]!;
    // Uma parada é longa por definição, mas não é lacuna: foi ouvida o tempo todo.
    if (a[0] - usaveis[i - 1]![0] > LACUNA_MS && a[4] !== PARADA) {
      if (atual.length >= 2) saida.push(atual);
      atual = [a];
    } else {
      atual.push(a);
    }
  }
  if (atual.length >= 2) saida.push(atual);
  return saida;
}

/** Quantos quilômetros a embarcação percorreu na trilha publicada. */
export function distanciaKm(trilha: readonly Amostra[] | undefined): number {
  if (!trilha || trilha.length < 2) return 0;
  let total = 0;
  for (const trecho of trechos(trilha)) {
    for (let i = 1; i < trecho.length; i++) {
      total += metros(trecho[i - 1]!, trecho[i]!);
    }
  }
  return total / 1000;
}

function metros(a: Amostra, b: Amostra): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const x = (b[2] - a[2]) * rad * Math.cos(((a[1] + b[1]) / 2) * rad);
  const y = (b[1] - a[1]) * rad;
  return Math.sqrt(x * x + y * y) * R;
}
